import {
  Box,
  Card,
  Skeleton,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import useAuth from "hooks/useAuth";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import svgImage from "../../../assets/logo/LoginBackground.png";
import { PostAsync } from "../../../common/httpRequests";
import { BACKEND } from "../../../constants/settings";
import { codeState } from "../../../features/pageSlice";
import CodeForm from "../form/CodeForm";
import EmailForm from "../form/EmailForm";
import { CustomTheme } from "enums/customTheme";
import { RootState } from "redux/store";

export default function EmailPageContainer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("placeholder@gmail.com");
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const page = useSelector((state: any) => state.page.value);
  const theme = useTheme();
  const { allowed } = useAuth();
  const customTheme = useSelector(
    (state: RootState) => state.supplier.supplier?.customTheme,
  );

  const handleSendEmail = async (event) => {
    if (event.target["email"]) {
      event.preventDefault();
      var capturedEmail = event.target["email"].value;
      setEmail(capturedEmail);
    } else {
      capturedEmail = email;
    }
    const currentSupplierId = window.location.pathname.split("/")[1];
    const request = {
      id: currentSupplierId,
      email: capturedEmail,
    };
    const res = await PostAsync(
      `${BACKEND}/api/vendorservice/suppliers/emailpasscode`,
      request,
    );
    if (res.status === 200 && event.target["email"]) {
      dispatch(codeState());
    } else if (res.status === 200) {
      return enqueueSnackbar(t("email.codeResent"), { variant: "success" });
    } else {
      return enqueueSnackbar(t("notFound"), {
        variant: "error",
      });
    }
  };

  function displayWarningIfSupplierNotFound() {
    if (allowed === "") {
      return (
        <Box display={"flex"} flexDirection={"column"} width="100%">
          <Skeleton variant="text" width="85%" height={40} />
          <Skeleton variant="text" width="45%" height={40} />
          <Skeleton variant="text" width="100%" height={30} />
          <Skeleton variant="text" width={60} height={30} />
          <Skeleton variant="text" width={"100%"} height={60} />
          <Skeleton variant="text" width={"100%"} height={60} />
        </Box>
      );
    } else if (!allowed) {
      return (
        <Box display={"flex"} flexDirection={"column"} maxWidth="500px">
          <Typography variant="h3" paddingBottom={2}>
            {t("notFound")}
          </Typography>
          <Typography gutterBottom>
            {t("notFoundctx")}{" "}
            <a
              href="mailto:support@mydost.ai"
              style={{
                textDecoration: "none",
                color: theme.palette.secondary.main,
              }}
            >
              support@mydost.ai
            </a>
          </Typography>
        </Box>
      );
    } else {
      return emailVerificationStep[page];
    }
  }
  const emailVerificationStep = {
    0: <EmailForm handleSendEmail={handleSendEmail} />,
    1: <CodeForm email={email} handleSendEmail={handleSendEmail} />,
  };

  return (
    <>
      {/* <EmailHeader /> */}
      <MainStyle>
        <RootStyle>
          <SectionStyle>
            <Box sx={{ display: "flex", maxWidth: "540px" }}>
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{
                  color:
                    customTheme === CustomTheme.Volotea
                      ? theme.palette.volotea.brown
                      : theme.palette.text.secondary,
                  lineHeight: 1.55,
                  fontSize: { md: 28, lg: 36 },
                }}
              >
                {t(`supplier_portal_tag_line`)}
              </Typography>
            </Box>
          </SectionStyle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "center", md: "flex-end" },
              flex: 1,
              minWidth: 0,
            }}
          >
            <ContentStyle>{displayWarningIfSupplierNotFound()}</ContentStyle>
          </Box>
        </RootStyle>
      </MainStyle>
    </>
  );
}

export const MainStyle = styled("main", {
  shouldForwardProp: (prop) => prop !== "collapseClick",
})(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  // AuthLayout adds 180px (mobile) / 132px (lg+) of vertical padding around this
  minHeight: "calc(100vh - 180px)",

  [theme.breakpoints.up("lg")]: {
    minHeight: "calc(100vh - 132px)",
    width: `calc(100%)`,
    transition: theme.transitions.create("margin-left", {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

export const RootStyle = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  display: "flex",
  width: "100%",
  padding: "0 24px",
  gap: "24px",
  [theme.breakpoints.up("md")]: {
    padding: "0 40px 0 0",
  },
  [theme.breakpoints.up("lg")]: {
    padding: "0 120px 0 0",
    gap: "40px",
  },
}));

export const SectionStyle = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  backgroundImage: `url(${svgImage})`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "contain",
  backgroundPosition: "left",
  [theme.breakpoints.up("md")]: {
    paddingLeft: 40,
  },
  [theme.breakpoints.up("lg")]: {
    paddingLeft: 120,
  },
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

export const ContentStyle = styled(Card)(({ theme }) => ({
  gap: "18px",
  width: "100%",
  maxWidth: 480,
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  padding: theme.spacing(4, 3),
  borderRadius: "16px",
  backgroundColor: "#FFFFFF",
  border: `1px solid #E5E7EB`,
  boxShadow: "0px 4px 24px 0px rgba(0, 0, 0, 0.10)",
  [theme.breakpoints.up("lg")]: {
    width: 480,
    minWidth: 480,
    padding: theme.spacing(5.5, 5),
  },
}));
